// MaturaPowerDivisibilityOpenTask.js
// Zadanie otwarte: dowód, że liczba (p^k)^{n} ± p^{m} jest podzielna przez p^{s} ∓ 1.
// Dział 10: Dowody matematyczne — dowód algebraiczny (podzielność)

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import EquationInput from "../../../../components/inputs/EquationInput";

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

// ============================================================================
// GENERATOR
// ============================================================================

/**
 * Wariant „oryginał” – matura podstawowa, sierpień 2025, zadanie 5:
 *   Wykaż, że 8^{50} - 2^{145} jest podzielna przez 31.
 *   8^{50} = 2^{150};  2^{150} - 2^{145} = 2^{145}(2^5 - 1) = 2^{145}·31.
 */
function createOriginalTask() {
  return {
    type: "MaturaPowerDivisibilityOpenTask",
    data: {
      primeBase: 2,
      k: 3,
      s: 5,
      base1: 8,
      base2: 2,
      exp1: 50,
      exp2: 145,
      divisor: 31,
      maxPoints: 2,
      sign: "minus",
      isPlus: false,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_SIE",
      taskNumber: 5,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();

  // wariant znaków: minus (p^s - 1) albo plus (p^s + 1)
  const sign = pick(["minus", "plus"]);
  const isPlus = sign === "plus";

  // konfiguracje, dla których dzielnik jest jedno- lub dwucyfrowy (< 100)

  // p^s - 1
  const configsMinus = [
    { primeBase: 2, s: 2 }, // 3
    { primeBase: 2, s: 3 }, // 7
    { primeBase: 2, s: 4 }, // 15
    { primeBase: 2, s: 5 }, // 31
    { primeBase: 3, s: 2 }, // 8
    { primeBase: 3, s: 3 }, // 26
    { primeBase: 3, s: 4 }, // 80
    { primeBase: 5, s: 2 }, // 24
  ];

  // p^s + 1
  const configsPlus = [
    { primeBase: 2, s: 2 }, // 5
    { primeBase: 2, s: 3 }, // 9
    { primeBase: 2, s: 4 }, // 17
    { primeBase: 2, s: 5 }, // 33
    { primeBase: 2, s: 6 }, // 65
    { primeBase: 3, s: 2 }, // 10
    { primeBase: 3, s: 3 }, // 28
    { primeBase: 3, s: 4 }, // 82
    { primeBase: 5, s: 2 }, // 26
  ];

  const configs = isPlus ? configsPlus : configsMinus;
  const { primeBase, s } = pick(configs); // p, s

  // k – potęga w base1
  const k = pick([2, 3]); // (p^k)^n

  const base1 = Math.pow(primeBase, k); // p^k
  const base2 = primeBase; // p

  // n dobieramy tak, żeby exp2 = k*n - s > 0, a różnica wykładników = s
  const possibleN = [6, 8, 10, 12, 15, 20, 25, 30];
  const exp1 = pick(possibleN);
  const exp2 = k * exp1 - s;

  // dzielnik: p^s ± 1 (zawsze < 100)
  const divisor = Math.pow(primeBase, s) + (isPlus ? 1 : -1);
  const maxPoints = 2;

  return {
    type: "MaturaPowerDivisibilityOpenTask",
    data: {
      primeBase,
      k,
      s,
      base1,
      base2,
      exp1,
      exp2,
      divisor,
      maxPoints,
      sign,   // "minus" | "plus"
      isPlus, // bool dla wygody
    },
    source: { variant: "random", basedOn: "MATURA_2025_SIE_5" },
  };
}

// ============================================================================
// RENDER TREŚCI ZADANIA
// ============================================================================

export function renderTask({ data }) {
  const { base1, exp1, base2, exp2, divisor, maxPoints, isPlus } = data;

  const op = isPlus ? "+" : "-";
  const exprLatex = `${base1}^{${exp1}} ${op} ${base2}^{${exp2}}`;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne – otwarte (0–{maxPoints}).</strong>
      </p>
      <p>
        Wykaż, że liczba{" "}
        <InlineMath math={exprLatex} /> jest podzielna przez{" "}
        <InlineMath math={String(divisor)} />.
      </p>
      <p>Skorzystaj z własności działań na potęgach. Wykonaj dwa kluczowe kroki:</p>
    </div>
  );
}

// ============================================================================
// INPUT – dwa kroki dowodu
// ============================================================================

export function renderInput(task, userAnswer, onChange, disabled) {
  const value = userAnswer || {};

  const handleStep1Change = (raw) => {
    onChange?.({ ...value, step1: raw });
  };

  const handleStep2Change = (raw) => {
    onChange?.({ ...value, step2: raw });
  };

  return (
    <div className="space-y-3" style={{ marginTop: 12, textAlign: "left" }}>
      {/* KROK 1 */}
      <div>
        <div style={{ fontSize: 13, marginBottom: 4 }}>
          <strong>Krok 1.</strong> Sprowadź obie potęgi do tej samej podstawy.
        </div>

        <EquationInput
          value={value.step1 || ""}
          onChange={handleStep1Change}
          disabled={disabled}
          // przykładowa różnica dwóch potęg o tej samej podstawie
          placeholder="np. 2^{18} − 2^{14}"
          showPreview
        />
      </div>

      {/* KROK 2 */}
      <div>
        <div style={{ fontSize: 13, marginBottom: 4 }}>
          <strong>Krok 2.</strong> Wyłącz wspólny czynnik i pokaż dzielnik.
        </div>

        <EquationInput
          value={value.step2 || ""}
          onChange={handleStep2Change}
          disabled={disabled}
          // tylko końcowy iloczyn z dzielnikiem
          placeholder="np. 2^{14}·15"
          showPreview
        />
      </div>
    </div>
  );
}

// ============================================================================
// SPRAWDZANIE – 0/1/2 pkt z częściowymi (jak na maturze)
// ============================================================================

export function checkAnswer({ data }, userAnswer) {
  const emptyResult = {
    isCorrect: false,
    score: 0,
    maxScore: 2,
    parts: { step1: false, step2: false },
  };

  if (!userAnswer || typeof userAnswer !== "object") {
    return emptyResult;
  }

  const step1 = (userAnswer.step1 || "").trim();
  const step2 = (userAnswer.step2 || "").trim();

  if (!step1 && !step2) {
    return emptyResult;
  }

  const { primeBase, k, exp1, exp2, divisor, isPlus } = data;

  const normalize = (txt) =>
    txt
      .replace(/\s/g, "")
      .toLowerCase()
      .replace(/\\cdot/g, "*")
      .replace(/[·×]/g, "*");

  const n1 = normalize(step1);
  const n2 = normalize(step2);

  const topExp = k * exp1; // np. 16 w 2^16
  const diffExp = topExp - exp2; // np. s w p^s

  const patterns = (p, e) => [`${p}^${e}`, `${p}^{${e}}`];

  const [top1, top2] = patterns(primeBase, topExp);
  const [mid1, mid2] = patterns(primeBase, exp2);
  const [diff1, diff2] = patterns(primeBase, diffExp);

  const opSymbol = isPlus ? "+" : "-";

  // ----- KROK 1: p^{topExp} ± p^{exp2} -----
  const hasTop_step1 = n1.includes(top1) || n1.includes(top2);
  const hasMid_step1 = n1.includes(mid1) || n1.includes(mid2);
  const hasSign_step1 = n1.includes(opSymbol);

  const step1Ok = hasTop_step1 && hasMid_step1 && hasSign_step1;

  // ----- KROK 2: forma rozkładu / lub sam iloczyn z dzielnikiem -----
  const hasMid_step2 = n2.includes(mid1) || n2.includes(mid2);
  const hasDiff_step2 = n2.includes(diff1) || n2.includes(diff2);
  const hasDivisor_step2 = n2.includes(String(divisor));
  const hasProductSign_step2 = n2.includes("*");
  const hasFactorBracket_step2 =
    n2.includes(`(${diff1}${opSymbol}1)`) ||
    n2.includes(`(${diff2}${opSymbol}1)`);

  // Wariant A: p^{exp2}(p^{diffExp} ± 1), z jawnym mnożeniem
  // albo z nawiasem bez znaku mnożenia.
  const step2FactorOk =
    hasMid_step2 &&
    hasDiff_step2 &&
    (hasProductSign_step2 || hasFactorBracket_step2);

  // Wariant B: końcowy iloczyn p^{exp2} · divisor.
  // Samo wystąpienie obu liczb nie wystarcza.
  const directProducts = [
    `${mid1}*${divisor}`,
    `${mid2}*${divisor}`,
    `${divisor}*${mid1}`,
    `${divisor}*${mid2}`,
  ];
  const step2ProductOk =
    hasMid_step2 &&
    hasDivisor_step2 &&
    directProducts.some((product) => n2.includes(product));

  const step2Ok = step2FactorOk || step2ProductOk;

  // ----- Punktacja -----
  let score = 0;

  if (step2Ok && !step1) {
    score = 2;
  } else if (step1Ok && step2Ok) {
    score = 2;
  } else if (step1Ok) {
    score = 1;
  } else if (step2Ok) {
    score = 2;
  } else {
    score = 0;
  }

  return {
    isCorrect: score === 2,
    score,
    maxScore: 2,
    parts: {
      step1: step1Ok,
      step2: step2Ok,
    },
  };
}

// ============================================================================
// MODEL ODPOWIEDZI (krótko – dwa kluczowe kroki, drugi to sam iloczyn)
// ============================================================================

export function renderAnswer({ data }) {
  const { primeBase, k, exp1, exp2, divisor, isPlus } = data;

  const topExp = k * exp1;
  const op = isPlus ? "+" : "-";

  // krok 1 – już po sprowadzeniu do tej samej podstawy
  const step1 = `${primeBase}^{${topExp}} ${op} ${primeBase}^{${exp2}}`;

  // krok 2 – tylko końcowy iloczyn z dzielnikiem
  const step2 = `${primeBase}^{${exp2}}\\cdot ${divisor}`;

  return (
    <div
      className="space-y-1"
      style={{
        textAlign: "left",
        width: "100%",
        display: "block",
      }}
    >
      <p>
        <InlineMath math={step1} />
      </p>
      <p>
        <InlineMath math={step2} />
      </p>
    </div>
  );
}

// ============================================================================
// PEŁNE WYJAŚNIENIE KROK PO KROKU
// ============================================================================

export function renderExplanation({ data }) {
  const {
    primeBase,
    k,
    exp1,
    exp2,
    divisor,
    base1,
    base2,
    s,
    isPlus,
  } = data;

  const topExp = k * exp1;
  const diffExp = topExp - exp2;
  const op = isPlus ? "+" : "-";

  const step1 = `${base1}^{${exp1}} = (${primeBase}^{${k}})^{${exp1}} = ${primeBase}^{${topExp}}`;
  const step2 = `${base1}^{${exp1}} ${op} ${base2}^{${exp2}} = ${primeBase}^{${topExp}} ${op} ${primeBase}^{${exp2}}`;

  const innerSign = isPlus ? "+" : "-";
  const step3 = `${primeBase}^{${topExp}} ${op} ${primeBase}^{${exp2}} = ${primeBase}^{${exp2}}\\left(${primeBase}^{${diffExp}} ${innerSign} 1\\right)`;

  const powerValue = Math.pow(primeBase, diffExp);
  const step4 = isPlus
    ? `${primeBase}^{${diffExp}} + 1 = ${powerValue} + 1 = ${divisor}`
    : `${primeBase}^{${diffExp}} - 1 = ${powerValue} - 1 = ${divisor}`;

  const step5 = `${primeBase}^{${exp2}}\\left(${primeBase}^{${diffExp}} ${innerSign} 1\\right) = ${primeBase}^{${exp2}}\\cdot ${divisor}`;

  return (
    <div
      className="space-y-3"
      style={{ textAlign: "left", width: "100%", display: "block" }}
    >
      <p>
        <strong>Krok 1.</strong> Sprowadzamy obie potęgi do tej samej podstawy:
      </p>
      <BlockMath math={step1} />

      <p>
        <strong>Krok 2.</strong> Podstawiamy do wyrażenia i zapisujemy sumę /
        różnicę potęg o tej samej podstawie:
      </p>
      <BlockMath math={step2} />

      <p>
        <strong>Krok 3.</strong> Wyłączamy wspólny czynnik{" "}
        <InlineMath math={`${primeBase}^{${exp2}}`} /> przed nawias:
      </p>
      <BlockMath math={step3} />

      <p>
        <strong>Krok 4.</strong> Obliczamy wartość w nawiasie:
      </p>
      <BlockMath math={step4} />

      <p>
        <strong>Krok 5.</strong> Otrzymujemy iloczyn z czynnikiem{" "}
        <InlineMath math={String(divisor)} />:
      </p>
      <BlockMath math={step5} />

      <p>
        <strong>Wniosek.</strong> Ponieważ liczba{" "}
        <InlineMath
          math={`${base1}^{${exp1}} ${op} ${base2}^{${exp2}}`}
        />{" "}
        ma postać{" "}
        <InlineMath math={`${primeBase}^{${exp2}}\\cdot ${divisor}`} />, jest
        ona podzielna przez <InlineMath math={String(divisor)} />.
      </p>
    </div>
  );
}

// ============================================================================
// PODGLĄD ODPOWIEDZI UCZNIA (KaTeX, z potęgami i mnożeniem)
// ============================================================================

function toUserLatex(str) {
  let s = String(str ?? "");

  // ułamki w stylu 3/4 -> \\dfrac{3}{4}
  s = s.replace(
    /(?<!\\dfrac\{)(-?\d+)\/(\d+)/g,
    (_, n, d) => `\\dfrac{${n}}{${d}}`
  );

  // wielocyfrowe wykładniki 2^16 -> 2^{16}, 2^-12 -> 2^{-12}
  s = s.replace(/\^(-?\d{2,})/g, "^{$1}");

  // mnożenie * i · -> \\cdot
  s = s.replace(/\*/g, "\\cdot ");
  s = s.replace(/·/g, "\\cdot ");

  return s;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || (!userAnswer.step1 && !userAnswer.step2)) {
    return <i>(brak odpowiedzi)</i>;
  }

  return (
    <div style={{ whiteSpace: "pre-wrap", marginTop: 8, textAlign: "left" }}>
      {userAnswer.step1 && (
        <p>
          <strong>Krok 1:</strong>{" "}
          <InlineMath math={toUserLatex(userAnswer.step1)} />
        </p>
      )}
      {userAnswer.step2 && (
        <p>
          <strong>Krok 2:</strong>{" "}
          <InlineMath math={toUserLatex(userAnswer.step2)} />
        </p>
      )}
    </div>
  );
}
